import { BehaviorSubject, Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { DirectMatchInviteRequest, DirectMatchInviteResponse, EmailMatchInviteRequest, EmailMatchInviteResponse, FootballNetworkCandidatesResponse, FootballReferencesResponse, FootballerFriendsResponse, GuestClaimInfo, GuestClaimInvitation, GuestClaimInvitationRequest, GuestClaimResult, GuestPlayerRequest, LanguageUpdateRequest, Player, PlayerMatchHistoryResponse, PlayerPushTokenInfo, PublicPlayerProfile, RatingUpdate, SupportedLanguage } from "../models/player";
import { ClientNotification } from "../models/clientNotification";
import { Event } from "../models/event";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { LogService } from "./log.service";
import { map, tap } from "rxjs/operators";
import { LatLng } from "app/shared/models/latlng";
import { Router } from "@angular/router";
import { GuestParticipationRequest, PlayerEventInfo } from "../models/playerEventInfo";
import { PendingRatingReview, PlayerRatingValue, RatingReviewProgress, RatingReviewSession } from "../models/ratingReview";
import { AnalyticsService } from "./analytics.service";
import { AuthRedirectService } from "./auth-redirect.service";

@Injectable()
export class PlayerService {
  public static DEFAULT_AVATAR_URL = environment.DEFAULT_AVATAR_URL;

  private player: Player;
  public readonly currentPlayer$ = new BehaviorSubject<Player>(null);
  private apiUrl: string = environment.API_BASE_URL + "/player";
  private apiUrlProfile: string = environment.API_BASE_URL + "/profile";
  private placeAutocompleteUrl: string = environment.API_BASE_URL + "/place-autocomplete";

  constructor(
    private authHttp: HttpClient,
    private logger: LogService,
    public router: Router,
    private analytics: AnalyticsService,
    private authRedirect: AuthRedirectService
  ) {
    if (localStorage.getItem(environment.EMAIL) && !this.isPublicAnonymousRoute()) {
      this.refreshUserInfo().subscribe(
        (player) => {
          this.logger.debug(`PlayerComponent -> refreshUserInfo() returned with player: ${player.email}`);
          this.setCurrentPlayer(player);
        },
        (error) => {
          this.logger.debug(`PlayerComponent -> refreshUserInfo() error: ${error}`);
          this.player = this.getCurrentPlayer();
        }
      );
    }
  }

  private isPublicAnonymousRoute(): boolean {
    const paths = [
      window.location.pathname,
      window.location.hash && window.location.hash.indexOf("#/") === 0 ? window.location.hash.substring(1) : "",
      this.router.url
    ];

    return paths.some(path => path && (
      path.indexOf("/claim/") === 0 ||
      path.indexOf("/join/") === 0 ||
      path.indexOf("/public-event-invite/") === 0 ||
      path.indexOf("/profile/") === 0 ||
      path.indexOf("/i/") === 0
    ));
  }

  public refreshUserInfo(): Observable<Player> {
    this.logger.debug(`PlayerService.refreshUserInfo()...`);
    return this.authHttp.get<Player>(
      `${this.apiUrl}/find?email=${localStorage.getItem(environment.EMAIL)}&data=${localStorage.getItem(environment.KEY)}`,
      this.getRequestOptions()
    );
  }

  public getCurrentUserProfile(): Observable<Player> {
    return this.authHttp.get<Player>(`${this.apiUrlProfile}/self`, this.getRequestOptions()).pipe(
      tap((player) => this.setCurrentPlayer(player))
    );
  }

  public getPublicProfile(playerId: string, contextEventId?: string, shareToken?: string): Observable<PublicPlayerProfile> {
    const baseOptions = this.getRequestOptions();
    const options = {
      ...baseOptions,
      headers: shareToken ? baseOptions.headers.set('X-Profile-Share-Token', shareToken) : baseOptions.headers,
      params: contextEventId ? new HttpParams().set("contextEventId", contextEventId) : undefined
    };
    return this.authHttp.get<PublicPlayerProfile>(`${this.apiUrl}/public/${playerId}`, options).pipe(
      map((profile) => PlayerService.normalizePlayerAvatar(profile))
    );
  }

  public createProfileShare(): Observable<{playerId: string; token: string; expiresAt: string}> {
    return this.authHttp.post<{playerId: string; token: string; expiresAt: string}>(`${this.apiUrl}/me/profile-share`, {}, this.getRequestOptions());
  }

  public getPlayerMatchHistory(playerId: string, page: number = 0, size: number = 20, sharedOnly: boolean = false, contextEventId?: string): Observable<PlayerMatchHistoryResponse> {
    let params = new HttpParams()
      .set("page", String(page))
      .set("size", String(size))
      .set("sharedOnly", String(sharedOnly));
    if (contextEventId) {
      params = params.set("contextEventId", contextEventId);
    }
    const options = {
      ...this.getRequestOptions(),
      params
    };

    return this.authHttp.get<PlayerMatchHistoryResponse>(`${this.apiUrl}/${playerId}/match-history`, options).pipe(
      map((response) => {
        (response.items || []).forEach((item) => {
          if (item.nextOccurrenceTimestamp) {
            item.nextOccurrenceTimestamp = new Date(item.nextOccurrenceTimestamp);
          }
          (item.participants || []).forEach((participant) => PlayerService.normalizePlayerAvatar(participant));
        });
        return response;
      })
    );
  }

  public updatePreferredLanguage(language: SupportedLanguage): Observable<Player> {
    const body: LanguageUpdateRequest = { language };
    return this.authHttp.put<Player>(`${this.apiUrl}/me/language`, JSON.stringify(body), this.getRequestOptions()).pipe(
      tap(() => this.analytics.track("language_selected", {
        screen: "profile",
        properties: { language }
      }))
    );
  }

  public checkCity(): boolean {
    const player = this.getCurrentPlayer();
    return !!(player && player.city);
  }

  public getCurrentPlayer(): Player {
    if (typeof this.player !== "undefined" && this.player && this.player.email) {
      return this.player;
    }

    if (!localStorage.getItem("player")) {
      this.logger.debug(`Unable to proceed without player data. Redirecting to authentication...`);
      this.authRedirect.login().subscribe();
    }

    this.player = PlayerService.normalizePlayerAvatar(JSON.parse(localStorage.getItem("player")));
    localStorage.setItem("player", JSON.stringify(this.player));
    this.currentPlayer$.next(this.player);

    this.logger.debug("PlayerService.getCurrentPlayer() returned player from local storage: " + this.player.email);

    return this.player;
  }

  public peekCurrentPlayer(): Player {
    if (this.player) {
      return this.player;
    }

    const storedPlayer = localStorage.getItem("player");
    if (!storedPlayer) {
      return null;
    }

    try {
      return PlayerService.normalizePlayerAvatar(JSON.parse(storedPlayer));
    } catch (error) {
      localStorage.removeItem("player");
      return null;
    }
  }

  public setCurrentPlayer(player: Player): void {
    this.player = this.mergeCurrentPlayer(player);
    PlayerService.normalizePlayerAvatar(this.player);
    localStorage.setItem("player", JSON.stringify(this.player));
    this.currentPlayer$.next(this.player);
  }

  public clearCurrentPlayer(): void {
    this.player = null;
    this.currentPlayer$.next(null);
  }

  private mergeCurrentPlayer(player: Player): Player {
    if (!player) {
      return player;
    }

    const incomingVerificationKnown = typeof player.emailVerified === "boolean" || typeof player.email_verified === "boolean";
    const existingVerified = this.player && (this.player.emailVerified === true || this.player.email_verified === true);

    if (!incomingVerificationKnown && existingVerified) {
      player.emailVerified = true;
      player.email_verified = true;
    }

    if (!player.profileStatsSummary && this.player && this.player.id === player.id && this.player.profileStatsSummary) {
      player.profileStatsSummary = this.player.profileStatsSummary;
    }

    return PlayerService.normalizePlayerAvatar(player);
  }

  public static normalizePlayerAvatar<T extends { avatar?: string }>(player: T): T {
    if (!player) {
      return player;
    }

    player.avatar = PlayerService.normalizedAvatarUrl(player.avatar);
    return player;
  }

  public static normalizedAvatarUrl(avatar: string): string {
    const candidate = avatar ? avatar.trim() : "";
    if (!candidate) {
      return PlayerService.DEFAULT_AVATAR_URL;
    }

    if (PlayerService.isExpiredFacebookProfilePicUrl(candidate)) {
      return PlayerService.DEFAULT_AVATAR_URL;
    }

    if (/^https?:\/\//i.test(candidate)
      || /^data:image\//i.test(candidate)
      || candidate.indexOf("assets/") === 0
      || candidate.indexOf("/assets/") === 0
      || candidate.indexOf("/api/v0/uploads/avatars/") === 0) {
      return candidate;
    }

    return PlayerService.DEFAULT_AVATAR_URL;
  }

  private static isExpiredFacebookProfilePicUrl(candidate: string): boolean {
    try {
      const url = new URL(candidate);
      if (url.hostname !== "platform-lookaside.fbsbx.com" || url.pathname.indexOf("/platform/profilepic") !== 0) {
        return false;
      }

      const expiresAt = Number(url.searchParams.get("ext"));
      return Number.isFinite(expiresAt) && expiresAt > 0 && expiresAt * 1000 < Date.now();
    } catch (error) {
      return false;
    }
  }

  public getEventsByEmail(playerEmail: string): Observable<Event[]> {
    return this.authHttp.get<Event[]>(`${this.apiUrl}/events/email/${playerEmail}`, this.getRequestOptions());
  }

  public getPlayers(partialNickName: string): Observable<Player[]> {
    return this.authHttp.get<Player[]>(`${this.apiUrl}/search?partialNickName=${partialNickName}`, this.getRequestOptions())
      .pipe(
        map((players: any) => {
          this.setPlayersAutocompleteName(players);
          return players;
        }));
  }

  public getPlayersOpenProfile(partialNickName: string): Observable<Player[]> {
    return this.authHttp.get<Player[]>(`${this.apiUrl}/search?partialNickName=${partialNickName}`, this.getRequestOptions())
      .pipe(
        map((players: any) => {
          this.setPlayersAutocompleteName(players);
          return players.filter(player => player.openProfile === true);
        }));
  }

  public getFootballerFriends(partialNickName: string = ""): Observable<Player[]> {
    const options = {
      ...this.getRequestOptions(),
      params: new HttpParams().set("partialNickName", partialNickName || "")
    };

    return this.authHttp.get<Player[]>(`${this.apiUrl}/me/footballer-friends`, options)
      .pipe(
        map((players: any) => {
          this.setPlayersAutocompleteName(players);
          return players;
        }));
  }

  public getBlockedPlayers(): Observable<any[]> {
    return this.authHttp.get<any[]>(`${this.apiUrl}/me/blocks`, this.getRequestOptions());
  }

  public blockPlayer(playerId: string): Observable<any> {
    return this.authHttp.post<any>(`${this.apiUrl}/me/blocks/${playerId}`, {}, this.getRequestOptions());
  }

  public unblockPlayer(playerId: string): Observable<void> {
    return this.authHttp.delete<void>(`${this.apiUrl}/me/blocks/${playerId}`, this.getRequestOptions());
  }

  public reportPlayer(playerId: string, reason: string, details: string = ""): Observable<void> {
    return this.authHttp.post<void>(`${this.apiUrl}/me/reports/${playerId}`, {
      reason,
      details: details || null,
      block: true
    }, this.getRequestOptions());
  }

  public getFootballerFriendsStats(partialNickName: string = "", page: number = 0, size: number = 20, sortBy: string = "matchesTogether", sortDirection: string = "desc"): Observable<FootballerFriendsResponse> {
    const options = {
      ...this.getRequestOptions(),
      params: new HttpParams()
        .set("partialNickName", partialNickName || "")
        .set("page", String(page))
        .set("size", String(size))
        .set("sortBy", sortBy || "matchesTogether")
        .set("sortDirection", sortDirection || "desc")
    };

    return this.authHttp.get<FootballerFriendsResponse>(`${this.apiUrl}/me/footballer-friends/stats`, options).pipe(
      map((response) => {
        (response.items || []).forEach((friend) => {
          PlayerService.normalizePlayerAvatar(friend);
          if (friend.lastSharedMatchAt) {
            friend.lastSharedMatchAt = new Date(friend.lastSharedMatchAt);
          }
        });
        return response;
      })
    );
  }

  public getFootballReferences(playerId: string, partialNickName: string = "", page: number = 0, size: number = 20, contextEventId?: string): Observable<FootballReferencesResponse> {
    let params = new HttpParams()
      .set("partialNickName", partialNickName || "")
      .set("page", String(page))
      .set("size", String(size));
    if (contextEventId) {
      params = params.set("contextEventId", contextEventId);
    }
    const options = {
      ...this.getRequestOptions(),
      params
    };

    return this.authHttp.get<FootballReferencesResponse>(`${this.apiUrl}/${playerId}/football-references`, options).pipe(
      map((response) => {
        (response.items || []).forEach((reference) => {
          PlayerService.normalizePlayerAvatar(reference);
          if (reference.lastPlayedWithCurrentAt) {
            reference.lastPlayedWithCurrentAt = new Date(reference.lastPlayedWithCurrentAt);
          }
          if (reference.lastPlayedWithViewedAt) {
            reference.lastPlayedWithViewedAt = new Date(reference.lastPlayedWithViewedAt);
          }
        });
        return response;
      })
    );
  }

  public getAddableNogoshPlayers(eventId: string, partialNickName: string): Observable<Player[]> {
    return this.getAddablePlayersFromEndpoint(eventId, partialNickName, "addable-nogosh-players");
  }

  public getAddableFootballerFriends(eventId: string, partialNickName: string): Observable<Player[]> {
    return this.getAddablePlayersFromEndpoint(eventId, partialNickName, "addable-footballer-friends");
  }

  public getAddableOpenProfiles(eventId: string, partialNickName: string): Observable<Player[]> {
    return this.getAddablePlayersFromEndpoint(eventId, partialNickName, "addable-open-profiles");
  }

  public getAddableFootballerNetwork(eventId: string, partialNickName: string): Observable<Player[]> {
    const options = {
      ...this.getRequestOptions(),
      params: new HttpParams().set("partialNickName", partialNickName || "").set("page", "0").set("size", "50")
    };
    return this.authHttp.get<FootballNetworkCandidatesResponse>(`${this.apiUrl}/events/${eventId}/addable-footballer-network`, options).pipe(
      map((response) => (response.items || []).map((candidate) => {
        const player = Object.assign(new Player(), candidate, {
          id: candidate.playerId,
          networkReferenceCount: candidate.referenceCount,
          networkReferenceScore: candidate.referenceScore,
          networkReferences: candidate.references || []
        });
        PlayerService.normalizePlayerAvatar(player);
        return player;
      }))
    );
  }

  public getDirectInviteCandidate(eventId: string, email: string, nickName: string): Observable<Player> {
    const options = {
      ...this.getRequestOptions(),
      params: new HttpParams()
        .set("email", email || "")
        .set("nickName", nickName || "")
    };

    return this.authHttp.get<Player>(`${this.apiUrl}/events/${eventId}/direct-invite-candidate`, options)
      .pipe(
        map((player: Player) => {
          this.setPlayersAutocompleteName([player]);
          return player;
        }));
  }

  public sendDirectMatchInvite(eventId: string, email: string, nickName: string): Observable<DirectMatchInviteResponse> {
    const body: DirectMatchInviteRequest = {
      email: email || null,
      nickName: nickName || null
    };

    return this.authHttp.post<DirectMatchInviteResponse>(`${this.apiUrl}/events/${eventId}/direct-invite`, JSON.stringify(body), this.getRequestOptions())
      .pipe(
        map((response: DirectMatchInviteResponse) => {
          if (response && response.player) {
            this.setPlayersAutocompleteName([response.player]);
          }
          return response;
        }));
  }

  public sendEmailMatchInvites(eventId: string, emails: string[]): Observable<EmailMatchInviteResponse> {
    const body: EmailMatchInviteRequest = { emails };
    return this.authHttp.post<EmailMatchInviteResponse>(`${this.apiUrl}/events/${eventId}/email-invites`, JSON.stringify(body), this.getRequestOptions());
  }

  private getAddablePlayersFromEndpoint(eventId: string, partialNickName: string, endpoint: string): Observable<Player[]> {
    const options = {
      ...this.getRequestOptions(),
      params: new HttpParams().set("partialNickName", partialNickName || "")
    };

    return this.authHttp.get<Player[]>(`${this.apiUrl}/events/${eventId}/${endpoint}`, options)
      .pipe(
        map((players: any) => {
          this.setPlayersAutocompleteName(players);
          return players;
        }));
  }

  public getCities(partialCityName: string): Observable<string[]> {
    const params = new HttpParams().set("q", partialCityName);
    return this.authHttp.get<any[]>(`${this.placeAutocompleteUrl}/cities`, { ...this.getRequestOptions(), params })
      .pipe(map((suggestions: any[]) => (suggestions || []).map((suggestion) => suggestion.label)));
  }

  public getAddress(partialAddres: string, lat: number, lng: number): Observable<string[]> {
    const params = new HttpParams()
      .set("q", partialAddres)
      .set("latitude", String(lat))
      .set("longitude", String(lng));
    return this.authHttp.get<any[]>(`${this.placeAutocompleteUrl}/addresses`, { ...this.getRequestOptions(), params })
      .pipe(map((suggestions: any[]) => (suggestions || []).map((suggestion) => suggestion.mainText || suggestion.label)));
  }

  public getLatAndLng(city: string) {
    return this.authHttp.get<LatLng>(`${this.apiUrl}/getGeoLocation?address=${city}`, this.getRequestOptions());
  }

  public getPlayer(email: string): Observable<Player> {
    this.logger.debug(`PlayerService.getPlayer(${email})...`);
    this.logger.debug(`${this.apiUrl}/searchplayer?playerEmail=${email}`);

    return this.authHttp.get<Player>(`${this.apiUrl}/find?email=${email}`, this.getRequestOptions());
  }

  public getPlayerByAuthId(authId: string): Observable<Player> {
    return this.authHttp.get<Player>(`${this.apiUrl}/find/auth?authId=${authId}`, this.getRequestOptions());
  }

  public updatePlayer(body: Object, playerId: string): Observable<Player> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.put<Player>(`${this.apiUrl}/${playerId}`, bodyString, this.getRequestOptions()).pipe(
      tap(() => this.analytics.track("profile_updated", {
        screen: "profile",
        entityType: "player",
        entityId: playerId
      }))
    );
  }

  public updateAvatar(file: File): Observable<Player> {
    const formData = new FormData();
    formData.append("file", file);
    return this.authHttp.put<Player>(`${this.apiUrl}/me/avatar`, formData).pipe(
      tap((player) => {
        this.setCurrentPlayer(player);
        this.analytics.track("profile_avatar_updated", {
          screen: "profile",
          entityType: "player",
          entityId: player && player.id
        });
      })
    );
  }

  public resetAvatar(): Observable<Player> {
    return this.authHttp.delete<Player>(`${this.apiUrl}/me/avatar`, this.getRequestOptions()).pipe(
      tap((player) => {
        this.setCurrentPlayer(player);
        this.analytics.track("profile_avatar_reset", {
          screen: "profile",
          entityType: "player",
          entityId: player && player.id
        });
      })
    );
  }

  public getGuestPlayers(eventId: string): Observable<Player[]> {
    return this.authHttp.get<Player[]>(`${this.apiUrl}/guest/event/${eventId}`, this.getRequestOptions()).pipe(
      map((players: any) => {
        this.setPlayersAutocompleteName(players);
        return players;
      })
    );
  }

  public createGuestPlayer(eventId: string, body: GuestPlayerRequest): Observable<Player> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.post<PlayerEventInfo>(`${this.apiUrl}/guest/event/${eventId}`, bodyString, this.getRequestOptions()).pipe(
      map((playerEventInfo) => playerEventInfo.player),
      tap((player) => this.analytics.track("guest_player_added", {
        screen: "match_players",
        entityType: "player",
        entityId: player && player.id
      }))
    );
  }

  public updateGuestPlayer(guestId: string, eventId: string, body: GuestPlayerRequest): Observable<Player> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.put<Player>(`${this.apiUrl}/guest/${guestId}/event/${eventId}/profile`, bodyString, this.getRequestOptions());
  }

  public createGuestClaimInvitation(guestId: string, body: GuestClaimInvitationRequest): Observable<GuestClaimInvitation> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.post<GuestClaimInvitation>(`${this.apiUrl}/guest/${guestId}/claim-invitation`, bodyString, this.getRequestOptions());
  }

  public getGuestClaimInvitation(guestId: string): Observable<GuestClaimInvitation> {
    return this.authHttp.get<GuestClaimInvitation>(`${this.apiUrl}/guest/${guestId}/claim-invitation`, this.getRequestOptions());
  }

  public resendGuestClaimInvitation(guestId: string): Observable<GuestClaimInvitation> {
    return this.authHttp.post<GuestClaimInvitation>(`${this.apiUrl}/guest/${guestId}/claim-invitation/resend`, {}, this.getRequestOptions());
  }

  public revokeGuestClaimInvitation(guestId: string): Observable<GuestClaimInvitation> {
    return this.authHttp.delete<GuestClaimInvitation>(`${this.apiUrl}/guest/${guestId}/claim-invitation`, this.getRequestOptions());
  }

  public getGuestClaimInfo(token: string): Observable<GuestClaimInfo> {
    return this.authHttp.get<GuestClaimInfo>(`${this.apiUrl}/guest/claim/${token}`, this.getRequestOptions());
  }

  public claimGuestProfile(token: string): Observable<GuestClaimResult> {
    return this.authHttp.post<GuestClaimResult>(`${this.apiUrl}/guest/claim/${token}`, {}, this.getRequestOptions()).pipe(
      tap(() => this.analytics.track("guest_claim_completed", { screen: "guest_claim" }))
    );
  }

  public sendEmailVerification(): Observable<{ message: string }> {
    return this.authHttp.post<{ message: string }>(`${this.apiUrlProfile}/email-verification`, {}, this.getRequestOptions());
  }

  public getClientNotifications(): Observable<ClientNotification[]> {
    return this.authHttp.get<ClientNotification[]>(`${this.apiUrlProfile}/client-notifications`, this.getRequestOptions());
  }

  public dismissClientNotification(notificationId: string): Observable<Player> {
    return this.authHttp.put<Player>(
      `${this.apiUrlProfile}/client-notifications/dismissed`,
      JSON.stringify({ notificationId }),
      this.getRequestOptions()
    ).pipe(
      tap((player) => this.setCurrentPlayer(player))
    );
  }

  public dismissClientNotifications(notificationIds: string[]): Observable<Player> {
    return this.authHttp.put<Player>(
      `${this.apiUrlProfile}/client-notifications/dismissed`,
      JSON.stringify({ notificationIds }),
      this.getRequestOptions()
    ).pipe(
      tap((player) => this.setCurrentPlayer(player))
    );
  }

  public markClientNotificationsRead(notificationIds: string[]): Observable<Player> {
    return this.authHttp.put<Player>(
      `${this.apiUrlProfile}/client-notifications/read`,
      JSON.stringify({ notificationIds }),
      this.getRequestOptions()
    ).pipe(
      tap((player) => this.setCurrentPlayer(player))
    );
  }

  public updateGuestParticipation(guestId: string, eventId: string, body: GuestParticipationRequest): Observable<PlayerEventInfo> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.put<PlayerEventInfo>(`${this.apiUrl}/guest/${guestId}/event/${eventId}`, bodyString, this.getRequestOptions());
  }

  public removeGuestFromEvent(guestId: string, eventId: string): Observable<string> {
    return this.authHttp.delete<string>(`${this.apiUrl}/guest/${guestId}/event/${eventId}`);
  }

  public updateChatMuteSettings(player: Player, currentEmail: string): Observable<Player> {
    let bodyString = JSON.stringify(player);
    return this.authHttp.put<Player>(`${this.apiUrl}/updateChatMuteSettings/${currentEmail}`, bodyString, this.getRequestOptions());
  }

  public updatePlayerEmail(currentEmail: string, newEmail: string): Observable<Player> {
    return this.authHttp.put<Player>(`${this.apiUrl}/updateEmail/${currentEmail}?newEmail=${newEmail}`, this.getRequestOptions()).pipe(
      tap((player) => this.analytics.track("profile_updated", {
        screen: "profile",
        entityType: "player",
        entityId: player && player.id
      }))
    );
  }

  public updatePlayerPushToken(pushTokenInfo: PlayerPushTokenInfo): Observable<any> {
    let bodyString = JSON.stringify(pushTokenInfo);
    return this.authHttp.put<Player>(`${environment.API_BASE_URL}/mobile/push-access-token`, bodyString, this.getRequestOptions());
  }

  public getPendingRatingReviews(): Observable<PendingRatingReview[]> {
    return this.authHttp.get<PendingRatingReview[]>(`${this.apiUrl}/me/rating-reviews/pending`, this.getRequestOptions());
  }

  public getRatingReview(reviewId: string): Observable<RatingReviewSession> {
    return this.authHttp.get<RatingReviewSession>(`${this.apiUrl}/me/rating-reviews/${reviewId}`, this.getRequestOptions()).pipe(
      map((review) => {
        if (review && review.players) {
          review.players.forEach((player) => PlayerService.normalizePlayerAvatar(player));
        }
        return review;
      })
    );
  }

  public updateRatingReviewPlayer(reviewId: string, playerId: string, rating: PlayerRatingValue): Observable<RatingReviewProgress> {
    let bodyString = JSON.stringify(rating);
    return this.authHttp.put<RatingReviewProgress>(
      `${this.apiUrl}/me/rating-reviews/${reviewId}/players/${playerId}`,
      bodyString,
      this.getRequestOptions()
    ).pipe(
      tap(() => this.analytics.track("rating_submitted", {
        screen: "rating_review",
        entityType: "rating_review",
        entityId: reviewId
      }))
    );
  }

  public skipRatingReviewPlayer(reviewId: string, playerId: string): Observable<RatingReviewProgress> {
    return this.authHttp.post<RatingReviewProgress>(
      `${this.apiUrl}/me/rating-reviews/${reviewId}/players/${playerId}/skip`,
      JSON.stringify({}),
      this.getRequestOptions()
    );
  }

  public completeRatingReview(reviewId: string): Observable<RatingReviewProgress> {
    return this.authHttp.post<RatingReviewProgress>(
      `${this.apiUrl}/me/rating-reviews/${reviewId}/complete`,
      JSON.stringify({}),
      this.getRequestOptions()
    );
  }

  public remindRatingReviewLater(reviewId: string): Observable<void> {
    return this.authHttp.post<void>(
      `${this.apiUrl}/me/rating-reviews/${reviewId}/remind-later`,
      JSON.stringify({}),
      this.getRequestOptions()
    );
  }

  public deletePlayerById(playerId: string, force: boolean = false): Observable<any> {
    const options = {
      ...this.getRequestOptions(),
      params: new HttpParams()
        .set("playerId", playerId)
        .set("force", String(force))
    };
    return this.authHttp.post<any>(`${this.apiUrlProfile}/deletePlayerById`, {}, options);
  }

  private setPlayersAutocompleteName(players: Player[]): void {
    for (let player of players) {
      PlayerService.normalizePlayerAvatar(player);
      player.autocompleteName = player.nickName;
    }
  }

  public getDetailCapabilities(playerId: string): Observable<{canRate: boolean; sharedPlayedMatchCount: number}> {
    return this.authHttp.get<{canRate: boolean; sharedPlayedMatchCount: number}>(`${this.apiUrl}/${playerId}/detail-capabilities`, this.getRequestOptions());
  }

  public updatePlayerDefenceRating(targetPlayerId: string, ratingDefenceUpdate: RatingUpdate): Observable<any> {
    const bodyString = JSON.stringify(ratingDefenceUpdate);
    return this.authHttp.put<Player>(`${this.apiUrl}/update-defence-rating/${targetPlayerId}`, bodyString, this.getRequestOptions());
  }

  public updatePlayerOffenceRating(targetPlayerId: string, ratingOffenceUpdate: RatingUpdate): Observable<any> {
    const bodyString = JSON.stringify(ratingOffenceUpdate);
    return this.authHttp.put<Player>(`${this.apiUrl}/update-offence-rating/${targetPlayerId}`, bodyString, this.getRequestOptions());
  }

  private getRequestOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
  }
}
